import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { countdownTo, pitchRows, slotSegments, type SlotKind } from './game';
import { UtCard, type UtCardPlayer } from './UtCard';
import { UT_TIERS, UT_TIER_ORDER, tierGradient } from './tiers';

const SLOT_BG: Record<SlotKind, string> = {
  xp: 'bg-ut-acc',
  random: 'bg-ut-blue',
  reserve: 'bg-ut-card2',
};

/** Registration slot bar: one segment per registrant (gold XP, blue random, grey reserve) plus a legend. */
export const SlotBar: React.FC<{ registered: number; maxPlayers: number; xpSlots: number; randomSlots: number }> = ({
  registered,
  maxPlayers,
  xpSlots,
  randomSlots,
}) => {
  const segs = slotSegments(registered, xpSlots, randomSlots);
  return (
    <div className="flex flex-col gap-2 rounded-xl border border-ut-line bg-ut-card px-4 py-3.5">
      <div className="flex justify-between text-[13px] font-semibold">
        <span>{registered} registered</span>
        <span className="text-ut-mut">{maxPlayers} slots</span>
      </div>
      <div className="flex h-2.5 gap-0.5" aria-hidden>
        {segs.length === 0 ? (
          <span className="flex-1 rounded-sm bg-ut-card2" />
        ) : (
          segs.map((k, i) => <span key={i} className={clsx('flex-1 rounded-sm', SLOT_BG[k])} />)
        )}
      </div>
      <div className="flex flex-wrap gap-3.5 text-xs text-ut-mut">
        <Legend className="bg-ut-acc">{xpSlots} by XP</Legend>
        <Legend className="bg-ut-blue">{randomSlots} random</Legend>
        <Legend className="bg-ut-card2">reserve</Legend>
      </div>
    </div>
  );
};

const Legend: React.FC<{ className: string; children: React.ReactNode }> = ({ className, children }) => (
  <span className="flex items-center gap-1.5">
    <span className={clsx('h-2 w-2 rounded-sm', className)} />
    {children}
  </span>
);

const pad = (n: number) => String(n).padStart(2, '0');

/** Three-box HRS / MIN / SEC countdown (days fold into hours). Ticks every second. */
export const CountdownBoxes: React.FC<{ target: string | Date | null | undefined; onDone?: () => void }> = ({ target, onDone }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const c = countdownTo(target, now);
  useEffect(() => {
    if (c.done) onDone?.();
    // onDone intentionally excluded: fire once when the countdown reaches zero.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c.done]);
  const boxes: [string, string][] = [
    [pad(c.days * 24 + c.hours), 'HRS'],
    [pad(c.minutes), 'MIN'],
    [pad(c.seconds), 'SEC'],
  ];
  return (
    <div className="grid grid-cols-3 gap-2" role="timer">
      {boxes.map(([v, l]) => (
        <div key={l} className="rounded-[10px] bg-ut-bg p-2.5 text-center">
          <div className="font-ut-display text-[32px] font-extrabold leading-none">{v}</div>
          <div className="text-[10px] font-semibold tracking-[0.14em] text-ut-mut">{l}</div>
        </div>
      ))}
    </div>
  );
};

/**
 * Pitch: two halves with a 2px pitch-line gap as the halfway line. Stacks on
 * mobile, side by side from ~760px. Rows of 3 cards, last row takes the rest.
 */
export const Pitch: React.FC<{
  halves: { name: string; players: UtCardPlayer[] }[];
  highlightId?: string | null;
}> = ({ halves, highlightId }) => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,370px),1fr))] gap-0.5 overflow-hidden rounded-2xl border-2 border-ut-pline bg-ut-pline">
    {halves.map((h) => (
      <div key={h.name} className="flex flex-col gap-2.5 bg-gradient-to-b from-ut-pitch to-ut-pitch2 px-2.5 py-3.5">
        <div className="text-center font-ut-display text-xs font-bold tracking-[0.14em] text-white opacity-85">{h.name}</div>
        {pitchRows(h.players).map((row, i) => (
          <div key={i} className="flex justify-center gap-2.5">
            {row.map((p) => (
              <UtCard key={p.id} player={p} size="pitch" highlight={p.id === highlightId} />
            ))}
          </div>
        ))}
        {h.players.length === 0 && <div className="py-8 text-center text-sm text-white/70">No players</div>}
      </div>
    ))}
  </div>
);

/** Tier legend pills. */
export const TierLegend: React.FC<{ className?: string }> = ({ className }) => (
  <div className={clsx('flex flex-wrap gap-1.5', className)}>
    {UT_TIER_ORDER.map((k) => {
      const t = UT_TIERS[k];
      return (
        <span key={k} className="flex items-center gap-1.5 rounded-full bg-ut-card py-[5px] pl-[5px] pr-2.5 text-xs font-semibold">
          <span className="h-3.5 w-3.5 rounded-full" style={{ background: tierGradient(t) }} />
          {t.label} <span className="font-medium text-ut-mut">{t.rule}</span>
        </span>
      );
    })}
  </div>
);

/** BLUE vs ORANGE header bar with a sub line under each side. */
export const TeamVsBar: React.FC<{ blueSub?: React.ReactNode; orangeSub?: React.ReactNode }> = ({ blueSub, orangeSub }) => (
  <div className="grid grid-cols-[1fr_auto_1fr] items-center overflow-hidden rounded-[14px] border border-ut-line">
    <div className="flex flex-col bg-ut-blue px-3.5 py-3 text-white">
      <span className="font-ut-display text-xl font-extrabold">BLUE</span>
      {blueSub && <span className="text-xs opacity-90">{blueSub}</span>}
    </div>
    <div className="flex items-center self-stretch bg-ut-card px-3 font-ut-display text-base font-extrabold text-ut-mut">VS</div>
    <div className="flex flex-col items-end bg-ut-orange px-3.5 py-3 text-white">
      <span className="font-ut-display text-xl font-extrabold">ORANGE</span>
      {orangeSub && <span className="text-xs opacity-90">{orangeSub}</span>}
    </div>
  </div>
);

export const TEAM_COLOR = { blue: '#2f78d6', orange: '#ec7a2e' } as const;
